import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { InputField, TextareaField } from '@/pages/dashboard/components/FormFields';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/upload';
import { initialsFor } from '@/lib/freelancers';

function FreelancerProfileEdit() {
  const { user } = useAuth();
  const [profileId, setProfileId] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [form, setForm] = useState({
    display_name: '',
    headline: '',
    location: '',
    phone: '',
    website: '',
    skills: '',
    bio: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('freelancer_profiles')
          .select('*')
          .eq('owner_id', user.id)
          .maybeSingle();
        if (error) throw error;
        if (mounted && data) {
          const d = data as {
            id: string;
            display_name: string | null;
            headline: string | null;
            location: string | null;
            phone: string | null;
            website: string | null;
            skills: string | null;
            bio: string | null;
            avatar_url: string | null;
          };
          setProfileId(d.id);
          setAvatarUrl(d.avatar_url || '');
          setForm({
            display_name: d.display_name || '',
            headline: d.headline || '',
            location: d.location || '',
            phone: d.phone || '',
            website: d.website || '',
            skills: d.skills || '',
            bio: d.bio || '',
          });
        }
      } catch (e) {
        if (mounted) {
          setStatus('error');
          setErrorMsg(e instanceof Error ? e.message : 'Impossible de charger votre profil.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingAvatar(true);
    setErrorMsg('');
    try {
      const url = await uploadFile(file, `freelancers/${user.id}`);
      setAvatarUrl(url);
      setStatus('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Échec de l'upload de la photo.");
      setStatus('error');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      display_name: form.display_name.trim() || null,
      headline: form.headline.trim() || null,
      location: form.location.trim() || null,
      phone: form.phone.trim() || null,
      website: form.website.trim() || null,
      skills: form.skills.trim() || null,
      bio: form.bio.trim() || null,
      avatar_url: avatarUrl || null,
    };

    let error;
    if (profileId) {
      const res = await supabase
        .from('freelancer_profiles')
        .update(payload)
        .eq('id', profileId);
      error = res.error;
    } else {
      const res = await supabase
        .from('freelancer_profiles')
        .insert({ ...payload, owner_id: user.id })
        .select('id')
        .single();
      error = res.error;
      if (!error && res.data) {
        setProfileId((res.data as { id: string }).id);
      }
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    setStatus('success');
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">
            Mon profil freelance
          </h1>
          <p className="text-sm text-foreground-400 mt-1">
            Présentez-vous aux clients : ce profil public s&apos;affiche sur votre page Work.
          </p>
        </div>
        {profileId && user && (
          <Link
            to={`/freelancers/${user.id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-100 transition-colors text-sm font-semibold whitespace-nowrap"
          >
            <i className="ri-external-link-line" />
            Voir mon profil public
          </Link>
        )}
      </div>

      <div className="max-w-2xl">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5"
          >
            <div className="flex items-center gap-4 pb-5 border-b border-background-200/70">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-background-200/60 shrink-0 flex items-center justify-center">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-heading font-bold text-2xl text-primary-400">
                    {initialsFor(form.display_name || 'Freelance')}
                  </span>
                )}
              </div>
              <div>
                <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-2-line" />
                  {uploadingAvatar ? 'Envoi...' : 'Changer la photo'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploadingAvatar}
                  />
                </label>
                <p className="text-xs text-foreground-500 mt-1">Format JPG ou PNG.</p>
              </div>
            </div>

            <InputField
              id="display_name"
              label="Nom affiché"
              icon="ri-user-line"
              value={form.display_name}
              onChange={(v) => updateField('display_name', v)}
              placeholder="Ex. Matthew DOTSE"
              hint="Le nom visible par les clients sur votre profil public."
            />

            <InputField
              id="headline"
              label="Titre professionnel"
              icon="ri-briefcase-line"
              value={form.headline}
              onChange={(v) => updateField('headline', v)}
              placeholder="Ex. Développeur Web Full-Stack"
            />

            <InputField
              id="location"
              label="Localisation"
              icon="ri-map-pin-line"
              value={form.location}
              onChange={(v) => updateField('location', v)}
              placeholder="Ex. Lomé, Togo"
            />

            <InputField
              id="phone"
              label="Téléphone"
              icon="ri-phone-line"
              value={form.phone}
              onChange={(v) => updateField('phone', v)}
              placeholder="Ex. +228 90 12 34 56"
              hint="Affiché publiquement pour que les clients vous contactent directement."
            />

            <InputField
              id="website"
              label="Site web"
              icon="ri-global-line"
              value={form.website}
              onChange={(v) => updateField('website', v)}
              placeholder="Ex. https://monsite.com"
              hint="Votre portfolio ou site personnel."
            />

            <InputField
              id="skills"
              label="Compétences"
              icon="ri-tools-line"
              value={form.skills}
              onChange={(v) => updateField('skills', v)}
              placeholder="Ex. React, TypeScript, SEO"
              hint="Séparez vos compétences par des virgules."
            />

            <TextareaField
              id="bio"
              label="Bio"
              icon="ri-quill-pen-line"
              value={form.bio}
              onChange={(v) => updateField('bio', v)}
              placeholder="Présentez votre parcours, votre métier et ce que vous proposez aux clients..."
              rows={5}
              maxLength={500}
            />

            {status === 'error' && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                {errorMsg}
              </div>
            )}
            {status === 'success' && (
              <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400">
                Profil freelance mis à jour avec succès.
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {saving ? 'Enregistrement...' : 'Enregistrer mon profil'}
              </button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}

export default FreelancerProfileEdit;